import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Button,
} from 'reactstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { updateUserDetails } from '../../services/api/doc-on-call';
import { getUserData } from '../../services/api/online-consultation-booking';
import { fetchUsersRelativeByphone } from '../../services/api/offline-consult';

export default function UpdateUserDetailsModal({ isOpen, toggle }) {
  const [type, setType] = useState('user');
  const [phone, setPhone] = useState('');
  const [fetchLoading, setFetchLoading] = useState(false);

  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState('');

  const [relativeOptions, setRelativeOptions] = useState([]);
  const [selectedRelative, setSelectedRelative] = useState(null);

  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const resetLookup = () => {
    setUserId(null);
    setUserName('');
    setRelativeOptions([]);
    setSelectedRelative(null);
  };

  const handleClose = () => {
    setType('user');
    setPhone('');
    setFetchLoading(false);
    resetLookup();
    setName('');
    setDob('');
    setGender('');
    setReason('');
    toggle();
  };

  const handleTypeChange = (e) => {
    setType(e.target.value);
    setPhone('');
    resetLookup();
  };

  const handlePhoneChange = (e) => {
    setPhone(e.target.value);
    resetLookup();
  };

  const handleFetch = () => {
    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setFetchLoading(true);

    if (type === 'user') {
      getUserData(phone)
        .then((res) => {
          if (res.message === 'success' && res.data && res.data[0]) {
            setUserId(res.data[0].userId);
            setUserName(res.data[0].userName || '');
          } else {
            toast.error(res.errorMessage || 'User not found');
          }
        })
        .catch((err) => {
          toast.error(err?.response?.data?.errorMessage || 'Failed to fetch user');
        })
        .finally(() => setFetchLoading(false));
    } else {
      fetchUsersRelativeByphone(phone)
        .then((res) => {
          if (res.userDetails && res.userDetails.userId) {
            setUserId(res.userDetails.userId);
            setUserName(res.userDetails.userName || '');
            const options = (res.data || []).map((el) => ({
              value: el.relativeId,
              label: el.relativeName,
            }));
            setRelativeOptions(options);
            if (options.length === 0) {
              toast.info('No relatives found for this number');
            }
          } else {
            toast.error('User not found');
          }
        })
        .catch((err) => {
          toast.error(err?.response?.data?.errorMessage || 'Failed to fetch relatives');
        })
        .finally(() => setFetchLoading(false));
    }
  };

  const handleSubmit = () => {
    if (!userId) {
      toast.error('Please fetch user details first');
      return;
    }
    if (type === 'relative' && !selectedRelative) {
      toast.error('Please select a relative');
      return;
    }
    if (!name.trim() && !dob && !gender) {
      toast.error('Please fill at least one field: Name, DOB, or Gender');
      return;
    }
    if (!reason.trim()) {
      toast.error('Please enter a reason for the update');
      return;
    }

    const payload = { type, userId, reason: reason.trim() };
    if (type === 'relative') payload.relativeId = selectedRelative.value;
    if (name.trim()) payload.name = name.trim();
    if (dob) payload.dob = dob;
    if (gender) payload.gender = gender;

    setLoading(true);
    updateUserDetails(payload)
      .then((response) => {
        if (response.message === 'success') {
          toast.success('Details updated successfully');
          handleClose();
        } else {
          toast.error(response.errorMessage || response.message || 'Update failed');
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.errorMessage || err.message || 'Update failed');
      })
      .finally(() => setLoading(false));
  };

  const userResolved = !!userId;
  const canSubmit =
    userResolved &&
    (type === 'user' || selectedRelative) &&
    (name.trim() || dob || gender) &&
    reason.trim();

  return (
    <Modal isOpen={isOpen} toggle={handleClose}>
      <ModalHeader toggle={handleClose}>Update User / Relative Details</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>Type</Label>
          <Input type="select" value={type} onChange={handleTypeChange}>
            <option value="user">User</option>
            <option value="relative">Relative</option>
          </Input>
        </FormGroup>

        <FormGroup>
          <Label>
            Phone Number <span style={{ color: 'red' }}>*</span>
          </Label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Input
              type="number"
              placeholder="Enter 10-digit phone number"
              value={phone}
              onChange={handlePhoneChange}
              style={{ flex: 1 }}
            />
            <Button
              color="secondary"
              onClick={handleFetch}
              disabled={fetchLoading || phone.length !== 10}
            >
              {fetchLoading ? 'Fetching...' : 'Fetch'}
            </Button>
          </div>
        </FormGroup>

        {userResolved && (
          <div
            style={{
              background: '#f0f4ff',
              border: '1px solid #d0d9f5',
              borderRadius: 6,
              padding: '8px 12px',
              marginBottom: 16,
              fontSize: 13,
              color: '#333',
            }}
          >
            <strong>User:</strong> {userName}{' '}
            <span style={{ color: '#6c757d' }}>(ID: {userId})</span>
          </div>
        )}

        {type === 'relative' && userResolved && (
          <FormGroup>
            <Label>
              Select Relative <span style={{ color: 'red' }}>*</span>
            </Label>
            <Select
              options={relativeOptions}
              value={selectedRelative}
              onChange={setSelectedRelative}
              placeholder={
                relativeOptions.length === 0
                  ? 'No relatives found'
                  : 'Select relative...'
              }
              isDisabled={relativeOptions.length === 0}
            />
          </FormGroup>
        )}

        {userResolved && (type === 'user' || selectedRelative) && (
          <>
            <FormGroup>
              <Label>Name</Label>
              <Input
                type="text"
                placeholder="Enter name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>Date of Birth</Label>
              <Input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label>Gender</Label>
              <Input
                type="select"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <option value="">-- Select --</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>
                Reason <span style={{ color: 'red' }}>*</span>
              </Label>
              <Input
                type="textarea"
                rows={3}
                placeholder="Enter reason for update"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </FormGroup>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={loading || !canSubmit}
        >
          {loading ? 'Updating...' : 'Update'}
        </Button>
        <Button color="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}
